/**
 * Shared helpers for ActiveJobs, ClosedJobs, and DraftJobs list views.
 * Extracted to keep behavior identical while avoiding duplicated logic.
 */

/**
 * Resolve the jobs/list/:user scope segment sent to the API.
 *
 * All-jobs scope (`'admin'`) is returned when the user has full-access roles
 * OR the narrow CASL subject `jobs_all` (read|manage). `jobs_all` only unlocks
 * the all-jobs list path — it does NOT grant admin/master/hr_head features.
 *
 * Grant for onboarding / HR (no admin|master|hr_head required):
 *   { "action": "read", "subject": "jobs" }
 *   { "action": "read", "subject": "jobs_all" }
 *   { "action": "create", "subject": "candidates" }
 *   // optional free pool:
 *   { "action": "create", "subject": "add_candidates" }
 *
 * Backend caveat: client reuses the existing all-jobs list path
 * (`GET jobs/list/admin`). API must accept that scope for these users;
 * prefer reusing that path rather than inventing a new scope string.
 */
export function resolveJobScopeUser(vm) {
  const empId = JSON.parse(localStorage.getItem('emp_id'))
  if (
    vm.$can('read', 'admin')
    || vm.$can('read', 'master')
    || vm.$can('read', 'hr_head')
    || vm.$can('read', 'jobs_all')
    || vm.$can('manage', 'jobs_all')
  ) {
    return 'admin'
  }
  if (vm.$can('read', 'program_manager') && vm.$can('read', 'vertical_data') && vm.$can('read', 'vertical_service') && !vm.$can('read', 'vertical_infra')) {
    return 'vertical_service'
  }
  if (vm.$can('read', 'program_manager') && vm.$can('read', 'vertical_data') && vm.$can('read', 'vertical_infra') && !vm.$can('read', 'vertical_service')) {
    return 'vertical_infra'
  }
  if (vm.$can('read', 'talent_acquisition') || vm.$can('read', 'account_manager') || vm.$can('read', 'sme_talent_acquisition') || vm.$can('read', 'hr') || (vm.$can('read', 'program_manager') && !vm.$can('read', 'vertical_data'))) {
    return empId
  }
  return vm.user || empId
}

export function getFilterTitle(value) {
  if (value && typeof value === 'object' && value.title) {
    return value.title
  }
  return value || ''
}

export function parseJsonArrayField(value) {
  if (!value) return []
  if (Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

export function parseAddedByTitle(addedBy) {
  try {
    const parsed = typeof addedBy === 'string' ? JSON.parse(addedBy) : addedBy
    if (Array.isArray(parsed) && parsed[0] && parsed[0].title) {
      return parsed[0].title
    }
  } catch (e) {
    // Ignore malformed addedBy payloads
  }
  return ''
}

export function getCityFilterSearchString(citySelected) {
  if (!citySelected) return ''

  try {
    const cloned = JSON.parse(JSON.stringify(citySelected))
    if (Array.isArray(cloned)) {
      return cloned.map(({ title }) => title).join(',')
    }
    if (cloned && typeof cloned === 'object' && cloned.title) {
      return cloned.title
    }
    return String(cloned)
  } catch (e) {
    return String(citySelected)
  }
}

export function buildFilteredJobList(jobData, filters) {
  if (!Array.isArray(jobData)) {
    return []
  }

  const clientTitle = getFilterTitle(filters.clientSelected)
  const domainTitle = getFilterTitle(filters.domainSelected)
  const cityTitle = getFilterTitle(filters.citySelected)
  const mgrTitle = getFilterTitle(filters.mgrSelected)

  if (
    (!clientTitle || clientTitle === 'All Clients')
    && (!domainTitle || domainTitle === 'All Domains')
    && (!cityTitle || cityTitle === 'All Cities')
    && (!mgrTitle || mgrTitle === 'All Account Managers')
  ) {
    return jobData
  }

  const citySearchStr = getCityFilterSearchString(filters.citySelected)

  return jobData.filter((object) => {
    if (!object) return false

    const objClient = object.client && object.client[0] ? object.client[0].title : object.client
    const objDomain = object.domain && object.domain[0] ? object.domain[0].title : object.domain
    const objMgr = object.account_mgr && object.account_mgr[0] ? object.account_mgr[0].title : object.account_mgr
    const objLocation = object.locations && object.locations[0] ? object.locations[0].title : (object.locations || '')

    return (
      (clientTitle && objClient == clientTitle)
      || (domainTitle && objDomain == domainTitle)
      || (mgrTitle && objMgr == mgrTitle)
      || (citySearchStr && objLocation.includes(citySearchStr))
    )
  })
}

export function buildCandidatePipeline(candidates, openCount) {
  const obj = {}
  if (candidates && candidates.length > 0) {
    for (let i = 0; i < candidates.length; i++) {
      const status = candidates[i].candidate_jobs && candidates[i].candidate_jobs.candidateStatus
      if (!status) continue
      if (!obj[status]) {
        obj[status] = 1
      } else {
        obj[status] += 1
      }
    }
  }

  obj.Offered = obj.Offered ? obj.Offered : 0
  obj.Joined = obj.Joined ? obj.Joined : 0
  obj.closed = obj.Offered + obj.Joined
  obj.opened = openCount - obj.closed
  return obj
}

export const jobListScrollRestoreMixin = {
  data() {
    return {
      scrollRestoreTimer: null,
    }
  },
  beforeDestroy() {
    if (this.scrollRestoreTimer) {
      clearTimeout(this.scrollRestoreTimer)
      this.scrollRestoreTimer = null
    }
  },
  updated() {
    this.$nextTick(() => {
      const scrollPosition = localStorage.getItem('lastPosition')
      if (!scrollPosition || scrollPosition === '[object Object]') {
        return
      }

      try {
        const y = JSON.parse(scrollPosition).y
        if (!y) {
          return
        }

        if (this.scrollRestoreTimer) {
          clearTimeout(this.scrollRestoreTimer)
        }

        this.scrollRestoreTimer = setTimeout(() => {
          window.scrollTo(0, y)
          this.scrollRestoreTimer = null
        }, 500)
      } catch (e) {
        // Ignore invalid saved scroll position
      }
    })
  },
}
